/**
 * Firebase API Service
 * 
 * Wrapper service for Firebase operations with standardized error handling,
 * retry logic, and offline support.
 */

import { firestoreLogger, withTiming } from './LoggingService';
import { firestore } from '../FirebaseConfig';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  addDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';

/**
 * Error types for Firebase operations
 */
export const FirebaseErrorType = {
  AUTH: 'auth',
  PERMISSION: 'permission',
  NOT_FOUND: 'not_found',
  NETWORK: 'network',
  QUOTA: 'quota',
  INVALID: 'invalid',
  UNKNOWN: 'unknown'
};

/**
 * Classify Firebase errors into standardized types
 */
export const classifyFirebaseError = (error) => {
  const code = error?.code || '';
  const message = error?.message?.toLowerCase() || '';

  // Permission errors
  if (code.includes('permission-denied') || code.includes('PERMISSION_DENIED')) {
    return {
      type: FirebaseErrorType.PERMISSION,
      message: "Ye don't have permission to access this treasure, matey.",
      retry: false
    };
  }

  // Authentication errors
  if (code.includes('unauthenticated') || code.includes('auth/')) {
    return {
      type: FirebaseErrorType.AUTH,
      message: 'Please log in to continue.',
      retry: false
    };
  }

  // Not found
  if (code.includes('not-found')) {
    return {
      type: FirebaseErrorType.NOT_FOUND,
      message: 'The requested document was not found.',
      retry: false
    };
  }

  // Network/offline
  if (
    code.includes('unavailable') ||
    message.includes('network') ||
    message.includes('offline')
  ) {
    return {
      type: FirebaseErrorType.NETWORK,
      message: 'Connection lost. Your changes will sync when back online.',
      retry: true
    };
  }

  // Quota exceeded
  if (code.includes('resource-exhausted')) {
    return {
      type: FirebaseErrorType.QUOTA,
      message: 'Service temporarily unavailable. Please try again later.',
      retry: true
    };
  }

  // Invalid argument
  if (code.includes('invalid-argument')) {
    return {
      type: FirebaseErrorType.INVALID,
      message: 'Invalid data provided.',
      retry: false
    };
  }

  return {
    type: FirebaseErrorType.UNKNOWN,
    message: 'An unexpected error occurred.',
    retry: true
  };
};

/**
 * Create a standardized Firebase error
 */
const createFirebaseError = (originalError, classified) => {
  const error = new Error(classified.message);
  error.name = 'FirebaseApiError';
  error.type = classified.type;
  error.originalError = originalError;
  error.code = originalError?.code;
  error.userMessage = classified.message;
  error.canRetry = classified.retry;
  return error;
};

/**
 * Execute a Firebase operation with error handling
 */
const firestoreCall = async (operation, operationName) => {
  try {
    return await withTiming(operationName, operation, firestoreLogger);
  } catch (error) {
    const classified = classifyFirebaseError(error);
    firestoreLogger.error(`${operationName} failed`, error, { errorType: classified.type });
    throw createFirebaseError(error, classified);
  }
};

/**
 * Firestore CRUD Operations
 */
export const FirestoreApi = {
  /**
   * Get a document by ID
   */
  async getDocument(collectionName, docId) {
    return firestoreCall(
      async () => {
        const docRef = doc(firestore, collectionName, docId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          return null;
        }
        
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      },
      `GetDocument:${collectionName}`
    );
  },

  /**
   * Create a new document
   */
  async createDocument(collectionName, data, docId = null) {
    return firestoreCall(
      async () => {
        const dataWithTimestamp = {
          ...data,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        if (docId) {
          const docRef = doc(firestore, collectionName, docId);
          await setDoc(docRef, dataWithTimestamp);
          return { id: docId, ...dataWithTimestamp };
        } else {
          const colRef = collection(firestore, collectionName);
          const docRef = await addDoc(colRef, dataWithTimestamp);
          return { id: docRef.id, ...dataWithTimestamp };
        }
      },
      `CreateDocument:${collectionName}`
    );
  },

  /**
   * Update a document
   */
  async updateDocument(collectionName, docId, data) {
    return firestoreCall(
      async () => {
        const docRef = doc(firestore, collectionName, docId);
        const updateData = {
          ...data,
          updatedAt: serverTimestamp()
        };
        await updateDoc(docRef, updateData);
        return { id: docId, ...updateData };
      },
      `UpdateDocument:${collectionName}`
    );
  },

  /**
   * Delete a document
   */
  async deleteDocument(collectionName, docId) {
    return firestoreCall(
      async () => {
        const docRef = doc(firestore, collectionName, docId);
        await deleteDoc(docRef);
        return { success: true, id: docId };
      },
      `DeleteDocument:${collectionName}`
    );
  },

  /**
   * Query documents
   */
  async queryDocuments(collectionName, filters = [], sortBy = null, limitCount = null) {
    return firestoreCall(
      async () => {
        let q = collection(firestore, collectionName);
        const constraints = [];

        // Add filters
        filters.forEach(({ field, operator, value }) => {
          constraints.push(where(field, operator, value));
        });

        // Add sorting
        if (sortBy) {
          const { field, direction = 'asc' } = sortBy;
          constraints.push(orderBy(field, direction));
        }

        // Add limit
        if (limitCount) {
          constraints.push(limit(limitCount));
        }

        if (constraints.length > 0) {
          q = query(q, ...constraints);
        }

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      },
      `QueryDocuments:${collectionName}`
    );
  },

  /**
   * Set up a real-time listener
   */
  subscribeToDocument(collectionName, docId, callback, errorCallback) {
    const docRef = doc(firestore, collectionName, docId);
    
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          callback({
            id: docSnap.id,
            ...docSnap.data()
          });
        } else {
          callback(null);
        }
      },
      (error) => {
        const classified = classifyFirebaseError(error);
        firestoreLogger.error('Subscription error', error, { 
          collection: collectionName, 
          docId 
        });
        if (errorCallback) {
          errorCallback(createFirebaseError(error, classified));
        }
      }
    );
  },

  /**
   * Subscribe to a query
   */
  subscribeToQuery(collectionName, filters = [], callback, errorCallback) {
    let q = collection(firestore, collectionName);
    const constraints = [];

    filters.forEach(({ field, operator, value }) => {
      constraints.push(where(field, operator, value));
    });

    if (constraints.length > 0) {
      q = query(q, ...constraints);
    }

    return onSnapshot(
      q,
      (querySnapshot) => {
        const docs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(docs);
      },
      (error) => {
        const classified = classifyFirebaseError(error);
        firestoreLogger.error('Query subscription error', error, { 
          collection: collectionName 
        });
        if (errorCallback) {
          errorCallback(createFirebaseError(error, classified));
        }
      }
    );
  }
};

/**
 * Specialized collection APIs
 */
export const BusinessListingsApi = {
  async getAll() {
    return FirestoreApi.queryDocuments('businessListings', [
      { field: 'status', operator: '==', value: 'active' }
    ]);
  },

  async getByOwner(ownerId) {
    return FirestoreApi.queryDocuments('businessListings', [
      { field: 'ownerId', operator: '==', value: ownerId }
    ]);
  },

  async getById(id) {
    return FirestoreApi.getDocument('businessListings', id);
  },

  async create(data) {
    return FirestoreApi.createDocument('businessListings', {
      ...data,
      status: 'active'
    });
  },

  async update(id, data) {
    return FirestoreApi.updateDocument('businessListings', id, data);
  },

  async delete(id) {
    return FirestoreApi.updateDocument('businessListings', id, {
      status: 'deleted',
      deletedAt: serverTimestamp()
    });
  }
};

export const TokensApi = {
  async getUserBalance(userId) {
    const doc = await FirestoreApi.getDocument('userTokens', userId);
    return doc?.balance || 0;
  },

  async addTokens(userId, amount, reason) {
    const current = await this.getUserBalance(userId);
    await FirestoreApi.updateDocument('userTokens', userId, {
      balance: current + amount
    });
    
    // Log transaction
    await FirestoreApi.createDocument('tokenTransactions', {
      userId,
      type: 'credit',
      amount,
      reason,
      balanceAfter: current + amount
    });

    return current + amount;
  },

  async spendTokens(userId, amount, reason) {
    const current = await this.getUserBalance(userId);
    
    if (current < amount) {
      throw new Error('Insufficient token balance');
    }

    await FirestoreApi.updateDocument('userTokens', userId, {
      balance: current - amount
    });

    // Log transaction
    await FirestoreApi.createDocument('tokenTransactions', {
      userId,
      type: 'debit',
      amount,
      reason,
      balanceAfter: current - amount
    });

    return current - amount;
  },

  async getTransactionHistory(userId, limitCount = 20) {
    return FirestoreApi.queryDocuments(
      'tokenTransactions',
      [{ field: 'userId', operator: '==', value: userId }],
      { field: 'createdAt', direction: 'desc' },
      limitCount
    );
  }
};

export default FirestoreApi;
